import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { FONTS } from '@/styles';
import { Text, TextProps, TextStyle } from 'react-native';

export interface Variants {
  heading: '4xl' | '3xl' | '2xl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs';
  label: 'lg' | 'md' | 'sm';
  paragraph: 'base' | 'base-600' | 'md' | 'md-600' | 'sm' | 'sm-600' | 'xs';
}

export type TypographyVariant = {
  [K in keyof Variants]: `${K}-${Variants[K]}`;
}[keyof Variants];

export interface TypographyProps extends Omit<TextProps, 'style'> {
  color?: string;
  flexShrink?: TextStyle['flexShrink'];
  textAlign?: TextStyle['textAlign'];
  textDecorationLine?: TextStyle['textDecorationLine'];
  textTransform?: 'capitalize' | 'uppercase' | 'lowercase';
  variant: TypographyVariant;
}

export const Typography: React.FC<TypographyProps> = ({
  children,
  color,
  textAlign,
  textDecorationLine,
  textTransform,
  flexShrink,
  variant,
  ...rest
}) => {
  const [styles] = useStyles();
  return (
    <Text
      style={[
        styles[variant],
        color !== undefined ? { color } : {},
        textTransform ? { textTransform } : {},
        textAlign ? { textAlign } : {},
        textDecorationLine ? { textDecorationLine } : {},
        flexShrink ? { flexShrink } : {},
      ]}
      {...rest}
    >
      {children}
    </Text>
  );
};

const FONT_STYLE_MAP: Record<number, TextStyle> = {
  400: FONTS.primary,
  600: FONTS.primarySemiBold,
};

// paragraph variants with fontWeight 400 use the secondary text color by default
const secondaryVariants: Set<TypographyVariant> = new Set([
  'paragraph-base',
  'paragraph-md',
  'paragraph-sm',
  'paragraph-xs',
]);

const isSecondary = (key: TypographyVariant) => secondaryVariants.has(key);

const useStyles = createStyles((theme) => {
  const result: Record<string, TextStyle> = {};

  const typography = theme.sk.typography;
  // input tokens are for TextInput styles only, not Typography variants
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { input: _input, ...textTypography } = typography;
  for (const type of Object.keys(textTypography) as Array<
    keyof typeof textTypography
  >) {
    const sizeMap = typography[type];

    for (const size of Object.keys(sizeMap) as Array<keyof typeof sizeMap>) {
      const key: TypographyVariant = `${type}-${size}`;

      const { fontSize, lineHeight, letterSpacing, fontWeight } = sizeMap[size];

      result[key] = {
        ...FONT_STYLE_MAP[fontWeight],
        fontSize,
        lineHeight,
        ...(letterSpacing !== undefined ? { letterSpacing } : {}),
        color: isSecondary(key)
          ? theme.sk.color.text.secondary
          : theme.sk.color.text.primary,
      };
    }
  }

  return result;
});
